import React, { useRef, useEffect, type KeyboardEvent, type ClipboardEvent, type ChangeEvent } from 'react';

interface OtpInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  dataTestId?: string;
}

export const OtpInput: React.FC<OtpInputProps> = ({
  length = 6,
  value,
  onChange,
  disabled = false,
  dataTestId,
}) => {
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  // Initialize refs array
  useEffect(() => {
    inputs.current = inputs.current.slice(0, length);
  }, [length]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const val = e.target.value;
    
    // Only allow numbers
    if (!/^\d*$/.test(val)) return;

    // Get the last character entered (in case user somehow enters multiple chars in one box not via paste)
    const char = val.slice(-1);

    const newValue = value.split('');
    newValue[index] = char;
    const nextValue = newValue.join('');
    
    onChange(nextValue);

    // Auto-focus next input if value is entered
    if (char && index < length - 1) {
      inputs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace') {
      if (!value[index] && index > 0) {
        // If empty and backspace pressed, move to previous
        inputs.current[index - 1]?.focus();
      } else if (value[index]) {
         // If has value, it will be deleted by onChange or we can clear it here and keep focus
         // Default behavior of input is fine, but we might want to move back after delete if it was empty?
         // Actually, standard behavior:
         // If current has value -> clear it, stay focused.
         // If current empty -> move prev, focus prev.
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputs.current[index + 1]?.focus();
    }
  };
  
  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, length).replace(/\D/g, '');
    if (pastedData) {
      onChange(pastedData);
      // Focus the input after the pasted content
      const nextIndex = Math.min(pastedData.length, length - 1);
      inputs.current[nextIndex]?.focus();
    }
  };

  return (
    <div className="flex gap-2 justify-center" data-test-id={dataTestId}>
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={(el) => { inputs.current[index] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          disabled={disabled}
          className={`w-12 h-14 text-center text-2xl font-bold border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${
            value[index] 
              ? 'border-primary-500 bg-white' 
              : 'border-gray-300 bg-gray-50'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          data-test-id={`${dataTestId}-${index}`}
        />
      ))}
    </div>
  );
};
